import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

/* Internal-link helper — descriptive anchor text, never "click here" */

/* ── Page 2 — expanded per /learn/what-is-tms-therapy-for-depression spec ──
   Server-rendered: all article text (H1, short answer, key takeaways, H2
   sections, FAQ answers, sources, review block) is emitted as semantic HTML
   by LearnPageLayout via entry-server.tsx (StaticRouter). No client-only
   loading, tabs, or hidden modal content — the full article is present in the
   initial server-delivered HTML for crawlers and AI search. ── */

const p2Faqs: LearnFAQ[] = [
  {
    question: "What is TMS therapy?",
    answer:
      "Transcranial magnetic stimulation (TMS) is a non-invasive treatment that uses magnetic pulses to stimulate a region of the brain involved in mood regulation. FDA-cleared TMS systems are used for certain adults with major depressive disorder, including some people whose symptoms have not adequately improved with other treatments. Patients remain awake during treatment, and no surgery or general anesthesia is required. Eligibility is determined by a qualified healthcare provider.",
  },
  {
    question: "Is TMS painful?",
    answer:
      "TMS is generally not described as painful. Most people feel a tapping or knocking sensation on the scalp during the magnetic pulses, with some discomfort during the first few sessions. Headache and scalp soreness are the most common side effects and usually fade within the first week. Facial muscles may twitch during pulses. Over-the-counter pain relievers and small adjustments to coil position or intensity help most patients. No anesthesia or sedation is needed.",
  },
  {
    question: "Is TMS FDA-cleared?",
    answer:
      "Yes. TMS devices are FDA-cleared through the 510(k) pathway, not FDA-approved. Approval is the process used for drugs; clearance is the process used for devices like TMS systems. The first TMS device, NeuroStar, was cleared in October 2008 for adults with major depressive disorder who did not improve enough on antidepressant medication in their current episode. Later device-specific clearances cover anxious depression, OCD, smoking cessation, and adolescents aged 15 to 21. Ask your clinic which device it uses and what that device is cleared for.",
  },
  {
    question: "How long does a TMS session take?",
    answer:
      "A single TMS session lasts from about 3 minutes with intermittent theta burst stimulation (iTBS) to about 40 minutes with older 10 Hz protocols. Including setup and positioning, a once-daily iTBS visit usually takes about 15 to 20 minutes. Your psychiatrist recommends a protocol based on your situation and your insurance plan's criteria.",
  },
  {
    question: "How many TMS treatments are needed?",
    answer:
      "A standard FDA-cleared course is about 30 to 36 sessions, delivered five days a week for roughly four to six weeks. Accelerated protocols such as SAINT® deliver multiple sessions per day over a shorter period. The total number of sessions depends on the protocol, your response, and your psychiatrist's recommendation.",
  },
  {
    question: "Can I drive after TMS?",
    answer:
      "Yes. Because TMS does not involve anesthesia or sedation and does not impair alertness, patients routinely drive themselves to and from sessions and return to work or normal activities immediately afterward. This is one of the main practical differences between TMS and ECT, which requires an escort home after each treatment.",
  },
  {
    question: "Does TMS require anesthesia?",
    answer:
      "No. TMS is delivered while you are awake and seated in a chair. No general anesthesia or sedation is used. The magnetic pulses pass through the scalp and skull to stimulate the brain without inducing a seizure. This is a key difference from electroconvulsive therapy (ECT), which is performed under general anesthesia.",
  },
  {
    question: "What is the difference between TMS and accelerated TMS?",
    answer:
      "Traditional TMS delivers one session a day, five days a week, for about four to six weeks. Accelerated TMS delivers two or more sessions a day, compressing the course into one or two weeks. The sessions themselves are usually short theta burst sessions of about 3 to 10 minutes with rest breaks between them. Only the SAINT® protocol has its own FDA clearance; most other accelerated schedules are off-label uses of FDA-cleared devices.",
  },
  {
    question: "Can TMS help if antidepressants have not worked?",
    answer:
      "TMS is FDA-cleared specifically for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. In studies, many such patients responded, but TMS does not work for everyone and individual results vary. A psychiatric evaluation is the best way to determine whether TMS is appropriate for your specific situation.",
  },
  {
    question: "Who should not receive TMS?",
    answer:
      "People with ferromagnetic metal in or near the head, such as cochlear implants, aneurysm clips, or certain stents, should not have TMS because the magnetic field could move or heat the metal. Implanted electronic stimulators are also a contraindication. A history of seizures requires careful evaluation and may rule out treatment. TMS is generally not used during a manic episode. A psychiatrist reviews all of these factors before treatment begins.",
  },
];

export const page2: LearnPageData = {
  slug: "what-is-tms-therapy-for-depression",
  title: "What Is TMS Therapy for Depression? | Neuro Wellness",
  description:
    "Learn how TMS therapy works for depression, who may qualify, treatment timelines, side effects, FDA clearance, and what patients can expect.",
  canonical: "/learn/what-is-tms-therapy-for-depression",
  h1: "What Is TMS Therapy for Depression?",
  breadcrumbLabel: "What Is TMS Therapy?",
  crumbs: baseCrumbs("What Is TMS Therapy?"),
  shortAnswer:
    "Transcranial magnetic stimulation (TMS) is a noninvasive treatment that uses magnetic pulses to stimulate areas of the brain involved in mood regulation. FDA-cleared TMS systems are used for certain patients with major depressive disorder, including some people whose symptoms have not adequately improved with other treatments. TMS does not require surgery or general anesthesia, and patients are typically awake during treatment. Eligibility should be determined by a qualified healthcare provider.",
  keyTakeaways: [
    "TMS is noninvasive — no surgery, no implants, no general anesthesia.",
    "TMS uses magnetic stimulation, not electrical current delivered directly into the brain.",
    "Patients remain awake and alert during treatment and can drive home afterward.",
    "Treatment is delivered over a series of sessions — typically 30 to 36 sessions across several weeks.",
    "Eligibility and outcomes vary by patient; a qualified provider determines whether TMS is appropriate.",
  ],

  sections: [
    {
      heading: "What Is TMS Therapy?",
      body: (
        <>
          {P(
            "Transcranial magnetic stimulation (TMS) is a non-invasive brain stimulation therapy for major depressive disorder. An electromagnetic coil placed on the scalp delivers repeated magnetic pulses to the left dorsolateral prefrontal cortex — a region involved in mood regulation. The magnetic field passes through the skull and induces small electrical currents in the cortex, increasing activity in an area that is often underactive in depression.",
          )}
          {P(
            "TMS devices are FDA-cleared for adults with major depressive disorder who have not improved enough on antidepressant medication in their current episode. The first device, NeuroStar, was cleared in October 2008. TMS is one of several depression treatment options and is not the same as electroconvulsive therapy (ECT).",
          )}
        </>
      ),
    },
    {
      heading: "How Does TMS Work for Depression?",
      body: (
        <>
          {P(
            "Depression is associated with reduced activity in the left dorsolateral prefrontal cortex, a brain region involved in mood regulation. TMS uses a focused magnetic field — similar in strength to an MRI — to stimulate this area. The magnetic pulses pass through the scalp and skull and induce small electrical currents in the cortex, which are thought to increase activity in the targeted region and its connected mood circuits.",
          )}
          {P(
            "Unlike medication, which affects the whole body through the bloodstream, TMS targets a specific brain region. Unlike ECT, TMS does not induce a seizure or require anesthesia. The stimulation is delivered while you sit awake in a chair, and you can return to normal activities immediately afterward.",
          )}
        </>
      ),
    },
    {
      heading: "Who May Be a Candidate for TMS?",
      body: (
        <>
          {P(
            "FDA-cleared TMS is intended for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. A psychiatrist evaluates factors such as diagnosis, medication history, and overall health before recommending treatment.",
          )}
          {P(
            "Eligibility also depends on safety screening. People with ferromagnetic metal in or near the head, implanted electronic devices, or certain seizure histories may not be candidates. TMS is generally not used during a manic episode. Because every situation is different, candidacy is determined by a qualified healthcare provider — not by a web page.",
          )}
        </>
      ),
    },
    {
      heading: "Is TMS FDA-Cleared for Depression?",
      body: (
        <div className="my-6 rounded-xl border border-medium-blue/30 bg-medium-blue/5 p-5">
          <p>
            <strong>FDA and regulatory information.</strong> TMS devices are
            FDA-cleared through the 510(k) pathway, not FDA-approved. Approval
            is the process used for drugs; clearance is the process used for
            devices like TMS systems. The first TMS device (NeuroStar) was
            cleared in October 2008 for adults with major depressive disorder
            who did not improve enough on antidepressant medication in their
            current episode. Later device-specific clearances cover anxious
            depression, OCD, smoking cessation, and adolescents aged 15 to 21.
          </p>
          <p className="mt-3">
            FDA clearance applies to specific devices and indications. It does
            not mean every patient is eligible for treatment or that a
            particular outcome is guaranteed. Ask your clinic which device it
            uses and what that device is cleared for.
          </p>
        </div>
      ),
    },
    {
      heading: "What Happens During a TMS Treatment Session?",
      body: (
        <>
          {P(
            "During a TMS session you sit in a reclined chair while a technician positions an electromagnetic coil against your scalp, usually over the left prefrontal cortex. You remain fully awake and alert. You will hear clicking sounds and feel a tapping or knocking sensation on your scalp as the magnetic pulses are delivered.",
          )}
          {P(
            "Hearing protection is worn because the coil is loud. The first session includes motor threshold mapping — finding the magnetic intensity that causes a small thumb twitch — so the treatment dose is tailored to you. After the session ends you can get up, drive yourself home, and return to work or normal activities right away.",
          )}
        </>
      ),
    },
    {
      heading: "How Long Does TMS Treatment Take?",
      body: (
        <>
          {P(
            "A standard FDA-cleared course is about 30 to 36 sessions, delivered five days a week for roughly four to six weeks. Each session lasts from about 3 minutes with intermittent theta burst stimulation (iTBS) to about 40 minutes with older 10 Hz protocols. Including setup and positioning, a typical visit takes about 15 to 45 minutes depending on the protocol.",
          )}
          {P(
            <>
              Accelerated protocols such as SAINT® deliver multiple sessions per
              day over a shorter overall period — five consecutive days instead
              of six weeks. Your psychiatrist recommends a protocol based on
              your situation and your insurance plan's criteria. See our
              overview of{" "}
              <a
                href="/contents/tms/typical-tms-treatment"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                what a typical TMS treatment course looks like
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "What Are the Potential Benefits of TMS?",
      body: (
        <>
          {P(
            "For many people whose depression has not responded to medication, TMS can reduce symptom severity and, in some cases, lead to remission. Because TMS is non-invasive and does not require anesthesia, it can be delivered in an outpatient setting with no driving restriction or recovery time. Many patients continue their daily routines — including work — throughout a standard course.",
          )}
          {P(
            "TMS may also be an option for people who cannot tolerate certain antidepressant side effects. However, benefits are not guaranteed and vary from person to person. A realistic treatment plan sets measurable goals and a reassessment date.",
          )}
        </>
      ),
    },
    {
      heading: "What Are the Side Effects and Risks of TMS?",
      body: (
        <>
          {P(
            "The most commonly reported side effects are scalp discomfort or pain at the treatment site, headache, and facial muscle twitching during stimulation. These usually lessen over the first week as you adjust to the sensation. Over-the-counter pain relievers and small adjustments to coil position or intensity help most patients.",
          )}
          {P(
            "The most serious known risk is seizure, which is rare — estimated at under 0.1% of sessions with standard parameters and proper screening. Hearing protection is worn because the coil is loud. Rarely, TMS can trigger manic symptoms in people with bipolar disorder, which is why mood history is screened before treatment.",
          )}
          {P(
            "TMS is not appropriate for everyone. People with ferromagnetic metal in or near the head (such as cochlear implants or aneurysm clips), implanted electronic stimulators, or a history of seizures require careful evaluation and may not be candidates. Tell your treatment team about any implants, seizure history, or medication changes.",
          )}
        </>
      ),
    },
    {
      heading: "How Effective Is TMS for Depression?",
      body: (
        <>
          {P(
            "Clinical studies have reported that TMS can reduce depression symptoms in many adults whose depression has not responded to medication, though it does not work for everyone. In a 301-person randomized sham-controlled trial, active TMS was superior to sham. In a 307-patient real-world naturalistic study, researchers observed about 58% response and 37% remission on clinician-rated scales.",
          )}
          {P(
            "These figures describe groups, not individuals. Roughly 40% or more of patients in real-world series do not meet response criteria, and some who improve later relapse. The naturalistic study had no control group. Individual results vary, and no treatment guarantees remission. Discuss your own likelihood of benefit with a psychiatrist.",
          )}
          {P(
            "The THREE-D trial (414 patients) found intermittent theta burst stimulation (iTBS) non-inferior to standard 10 Hz TMS, supporting shorter 3-minute sessions as an equivalent once-daily option.",
          )}
        </>
      ),
    },
    {
      heading: "TMS vs. Antidepressant Medication",
      body: (
        <>
          {P(
            "Antidepressant medication affects the whole body through the bloodstream and works over weeks as brain chemistry adjusts. TMS targets a specific brain region with magnetic pulses and does not involve drugs. For many patients, TMS is considered when antidepressants have not produced adequate improvement.",
          )}
          {P(
            <>
              TMS and medication are not mutually exclusive — many people
              continue an antidepressant during TMS. The two can be combined,
              and a psychiatrist determines the best approach. Learn more about{" "}
              <a
                href="/contents/conditions/depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                depression and its treatment options
              </a>{" "}
              or about{" "}
              <a
                href="/learn/what-is-treatment-resistant-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                treatment-resistant depression
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Traditional TMS vs. Accelerated TMS",
      body: (
        <>
          {P(
            "Traditional TMS delivers one session a day, five days a week, for about four to six weeks. Accelerated TMS delivers multiple sessions per day, compressing the course into a shorter period. Neither is universally better — each has different schedules, settings, and eligibility considerations.",
          )}
          {P(
            <>
              The comparison below summarizes the general differences.
              Individual protocols vary by clinic and device. Only the SAINT®
              protocol has its own FDA clearance as an accelerated treatment;
              most other accelerated schedules are off-label uses of FDA-cleared
              devices. See our guide to{" "}
              <a
                href="/learn/accelerated-tms-theta-burst-stimulation"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                accelerated TMS and theta burst stimulation
              </a>{" "}
              for more detail.
            </>,
          )}
          <div className="my-6 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-3 py-2 font-semibold">Factor</th>
                  <th className="px-3 py-2 font-semibold">Traditional TMS</th>
                  <th className="px-3 py-2 font-semibold">Accelerated TMS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr className="bg-card">
                  <td className="px-3 py-2 font-medium text-primary">
                    Treatment schedule
                  </td>
                  <td className="px-3 py-2">1 session/day, 5 days/week</td>
                  <td className="px-3 py-2">Multiple sessions/day</td>
                </tr>
                <tr className="bg-muted/20">
                  <td className="px-3 py-2 font-medium text-primary">
                    Sessions per day
                  </td>
                  <td className="px-3 py-2">1</td>
                  <td className="px-3 py-2">2 to 10</td>
                </tr>
                <tr className="bg-card">
                  <td className="px-3 py-2 font-medium text-primary">
                    Overall timeline
                  </td>
                  <td className="px-3 py-2">~4 to 6 weeks</td>
                  <td className="px-3 py-2">~1 to 2 weeks</td>
                </tr>
                <tr className="bg-muted/20">
                  <td className="px-3 py-2 font-medium text-primary">
                    Treatment setting
                  </td>
                  <td className="px-3 py-2">Outpatient clinic</td>
                  <td className="px-3 py-2">Outpatient clinic</td>
                </tr>
                <tr className="bg-card">
                  <td className="px-3 py-2 font-medium text-primary">
                    Patient experience
                  </td>
                  <td className="px-3 py-2">Short daily visits; drive home</td>
                  <td className="px-3 py-2">Full days at clinic with breaks</td>
                </tr>
                <tr className="bg-muted/20">
                  <td className="px-3 py-2 font-medium text-primary">
                    Eligibility
                  </td>
                  <td className="px-3 py-2">
                    Adults with MDD not helped by medication
                  </td>
                  <td className="px-3 py-2">Varies; SAINT® requires fMRI</td>
                </tr>
              </tbody>
            </table>
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Accelerated TMS is not always better; the right choice depends on
              your diagnosis, schedule, and insurance.
            </p>
          </div>
        </>
      ),
    },
    {
      heading: "What Questions Should I Ask My Provider?",
      body: (
        <>
          {P(
            "Asking the right questions helps you understand whether TMS is appropriate for you and what to expect. Consider asking your provider:",
          )}
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Which TMS device do you use, and what is it FDA-cleared for?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              How many sessions will I need, and how long is each one?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What are the common side effects, and how are they managed?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              How will my response be measured during and after treatment?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What is the plan if I do not improve?
            </li>
          </ul>
        </>
      ),
    },
  ],

  faqs: p2Faqs,
  sources: [
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of transcranial magnetic stimulation in the acute treatment of major depression: a multisite randomized controlled trial. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17574072/",
    },
    {
      citation:
        "Carpenter LL, et al. Transcranial magnetic stimulation (TMS) for major depression: a multisite, naturalistic, observational study of acute treatment outcomes in clinical practice. Depress Anxiety. 2012;29(7):587-596.",
      url: "https://pubmed.ncbi.nlm.nih.gov/22688299/",
    },
    {
      citation:
        "Blumberger DM, et al. Effectiveness of theta burst versus high-frequency repetitive transcranial magnetic stimulation in patients with depression (THREE-D): a randomised non-inferiority trial. Lancet. 2018;391(10131):1683-1692.",
      url: "https://doi.org/10.1016/S0140-6736(18)30295-2",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — NeuroStar TMS System (FDA clearance, October 2008).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "National Institute of Mental Health (NIMH). Depression (major depressive disorder) overview.",
      url: "https://www.nimh.nih.gov/health/topics/depression",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/what-is-tms-therapy-for-depression#webpage`,
        name: "What Is TMS Therapy for Depression?",
        url: `${SITE_URL}/learn/what-is-tms-therapy-for-depression`,
        description:
          "Learn how TMS therapy works for depression, who may qualify, treatment timelines, side effects, FDA clearance, and what patients can expect.",
        about: [
          {
            "@id": `${SITE_URL}/learn/what-is-tms-therapy-for-depression#therapy`,
          },
          { "@id": `${SITE_URL}/#condition-mdd` },
          { "@id": `${SITE_URL}/#condition-trd` },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/what-is-tms-therapy-for-depression#therapy`,
        name: "Transcranial magnetic stimulation (TMS) for depression",
        alternateName: [
          "Repetitive transcranial magnetic stimulation (rTMS)",
          "TMS therapy",
          "Magnetic brain stimulation therapy",
        ],

        description:
          "A non-invasive brain stimulation therapy in which an electromagnetic coil placed on the scalp delivers repeated magnetic pulses to the left dorsolateral prefrontal cortex, inducing small electrical currents in the cortex. TMS devices are FDA-cleared (510(k)) for major depressive disorder in adults who have failed to achieve satisfactory improvement from prior antidepressant medication in the current episode.",
        relevantSpecialty: "Psychiatry",
        indication: {
          "@type": "MedicalIndication",
          name: "Major depressive disorder in adult patients who have failed to achieve satisfactory improvement from prior antidepressant medication in the current episode",
        },
        contraindication: [
          {
            "@type": "MedicalContraindication",
            name: "Ferromagnetic metal in or near the head (e.g., cochlear implants, aneurysm clips, some stents)",
          },
          {
            "@type": "MedicalContraindication",
            name: "Implanted electronic stimulators",
          },
          {
            "@type": "MedicalContraindication",
            name: "History of seizures (requires careful evaluation)",
          },
        ],

        seriousAdverseOutcome: {
          "@type": "MedicalEntity",
          name: "Seizure (rare; estimated under 0.1% of sessions with standard parameters)",
        },
        adverseOutcome: [
          {
            "@type": "MedicalEntity",
            name: "Scalp discomfort or pain at the stimulation site",
          },
          { "@type": "MedicalEntity", name: "Headache" },
          { "@type": "MedicalEntity", name: "Facial muscle twitching" },
        ],

        study: [
          {
            "@type": "MedicalStudy",
            name: "O'Reardon JP, et al. Efficacy and safety of transcranial magnetic stimulation in the acute treatment of major depression: a multisite randomized controlled trial. Biol Psychiatry. 2007;62(11):1208-1216.",
            url: "https://pubmed.ncbi.nlm.nih.gov/17574072/",
          },
          {
            "@type": "MedicalStudy",
            name: "Carpenter LL, et al. Transcranial magnetic stimulation (TMS) for major depression: a multisite, naturalistic, observational study of acute treatment outcomes in clinical practice. Depress Anxiety. 2012;29(7):587-596.",
            url: "https://pubmed.ncbi.nlm.nih.gov/22688299/",
          },
          {
            "@type": "MedicalStudy",
            name: "Blumberger DM, et al. Effectiveness of theta burst versus high-frequency repetitive transcranial magnetic stimulation in patients with depression (THREE-D): a randomised non-inferiority trial. Lancet. 2018;391(10131):1683-1692.",
            url: "https://doi.org/10.1016/S0140-6736(18)30295-2",
          },
        ],
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-trd`,
        name: "Treatment-resistant depression",
      },
      faqJsonld(
        p2Faqs,
        `${SITE_URL}/learn/what-is-tms-therapy-for-depression#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("What Is TMS Therapy?")),
    ],
  },
  relatedLinks: sharedRelated,
};
